var _initialState, _revertState, _cache, _client, _retryer, _defaultOptions, _abortSignalConsumed, _Class_brand;
function _classPrivateMethodInitSpec(e, a) { _checkPrivateRedeclaration(e, a), a.add(e); }
function _classPrivateFieldInitSpec(e, t, a) { _checkPrivateRedeclaration(e, t), t.set(e, a); }
function _checkPrivateRedeclaration(e, t) { if (t.has(e)) throw new TypeError("Cannot initialize the same private elements twice on an object"); }
function _classPrivateFieldGet(s, a) { return s.get(_assertClassBrand(s, a)); }
function _classPrivateFieldSet(s, a, r) { return s.set(_assertClassBrand(s, a), r), r; }
function _assertClassBrand(e, t, n) { if ("function" == typeof e ? e === t : e.has(t)) return arguments.length < 3 ? t : n; throw new TypeError("Private element is not present on this object"); }
// src/query.ts
import { ensureQueryFn, noop, replaceData, resolveEnabled, resolveStaleTime, skipToken, timeUntilStale } from "./utils.js";
import { notifyManager } from "./notifyManager.js";
import { canFetch, createRetryer, isCancelledError } from "./retryer.js";
import { Removable } from "./removable.js";
var Query = (_initialState = /*#__PURE__*/new WeakMap(), _revertState = /*#__PURE__*/new WeakMap(), _cache = /*#__PURE__*/new WeakMap(), _client = /*#__PURE__*/new WeakMap(), _retryer = /*#__PURE__*/new WeakMap(), _defaultOptions = /*#__PURE__*/new WeakMap(), _abortSignalConsumed = /*#__PURE__*/new WeakMap(), _Class_brand = /*#__PURE__*/new WeakSet(), class Query extends Removable {
  constructor(config) {
    var _config$state;
    super();
    _classPrivateMethodInitSpec(this, _Class_brand);
    _classPrivateFieldInitSpec(this, _initialState, void 0);
    _classPrivateFieldInitSpec(this, _revertState, void 0);
    _classPrivateFieldInitSpec(this, _cache, void 0);
    _classPrivateFieldInitSpec(this, _client, void 0);
    _classPrivateFieldInitSpec(this, _retryer, void 0);
    _classPrivateFieldInitSpec(this, _defaultOptions, void 0);
    _classPrivateFieldInitSpec(this, _abortSignalConsumed, void 0);
    _classPrivateFieldSet(_abortSignalConsumed, this, false);
    _classPrivateFieldSet(_defaultOptions, this, config.defaultOptions);
    this.setOptions(config.options);
    this.observers = [];
    _classPrivateFieldSet(_client, this, config.client);
    _classPrivateFieldSet(_cache, this, _classPrivateFieldGet(_client, this).getQueryCache());
    this.queryKey = config.queryKey;
    this.queryHash = config.queryHash;
    _classPrivateFieldSet(_initialState, this, getDefaultState(this.options));
    this.state = (_config$state = config.state) !== null && _config$state !== void 0 ? _config$state : _classPrivateFieldGet(_initialState, this);
    this.scheduleGc();
  }
  get meta() {
    return this.options.meta;
  }
  get promise() {
    var _classPrivateFieldGet2;
    return (_classPrivateFieldGet2 = _classPrivateFieldGet(_retryer, this)) === null || _classPrivateFieldGet2 === void 0 ? void 0 : _classPrivateFieldGet2.promise;
  }
  setOptions(options) {
    this.options = {
      ..._classPrivateFieldGet(_defaultOptions, this),
      ...options
    };
    this.updateGcTime(this.options.gcTime);
  }
  optionalRemove() {
    if (!this.observers.length && this.state.fetchStatus === "idle") {
      _classPrivateFieldGet(_cache, this).remove(this);
    }
  }
  setData(newData, options) {
    const data = replaceData(this.state.data, newData, this.options);
    _assertClassBrand(_Class_brand, this, _dispatch).call(this, {
      data,
      type: "success",
      dataUpdatedAt: options === null || options === void 0 ? void 0 : options.updatedAt,
      manual: options === null || options === void 0 ? void 0 : options.manual
    });
    return data;
  }
  setState(state, setStateOptions) {
    _assertClassBrand(_Class_brand, this, _dispatch).call(this, {
      type: "setState",
      state,
      setStateOptions
    });
  }
  cancel(options) {
    var _classPrivateFieldGet3, _classPrivateFieldGet4;
    const promise = (_classPrivateFieldGet3 = _classPrivateFieldGet(_retryer, this)) === null || _classPrivateFieldGet3 === void 0 ? void 0 : _classPrivateFieldGet3.promise;
    (_classPrivateFieldGet4 = _classPrivateFieldGet(_retryer, this)) === null || _classPrivateFieldGet4 === void 0 || _classPrivateFieldGet4.cancel(options);
    return promise ? promise.then(noop).catch(noop) : Promise.resolve();
  }
  destroy() {
    super.destroy();
    this.cancel({
      silent: true
    });
  }
  reset() {
    this.destroy();
    this.setState(_classPrivateFieldGet(_initialState, this));
  }
  isActive() {
    return this.observers.some(observer => resolveEnabled(observer.options.enabled, this) !== false);
  }
  isDisabled() {
    if (this.getObserversCount() > 0) {
      return !this.isActive();
    }
    return this.options.queryFn === skipToken || this.state.dataUpdateCount + this.state.errorUpdateCount === 0;
  }
  isStatic() {
    if (this.getObserversCount() > 0) {
      return this.observers.some(observer => resolveStaleTime(observer.options.staleTime, this) === "static");
    }
    return false;
  }
  isStale() {
    if (this.getObserversCount() > 0) {
      return this.observers.some(observer => observer.getCurrentResult().isStale);
    }
    return this.state.data === void 0 || this.state.isInvalidated;
  }
  isStaleByTime() {
    let staleTime = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    if (this.state.data === void 0) {
      return true;
    }
    if (staleTime === "static") {
      return false;
    }
    if (this.state.isInvalidated) {
      return true;
    }
    return !timeUntilStale(this.state.dataUpdatedAt, staleTime);
  }
  onFocus() {
    var _classPrivateFieldGet5;
    const observer = this.observers.find(x => x.shouldFetchOnWindowFocus());
    observer === null || observer === void 0 || observer.refetch({
      cancelRefetch: false
    });
    (_classPrivateFieldGet5 = _classPrivateFieldGet(_retryer, this)) === null || _classPrivateFieldGet5 === void 0 || _classPrivateFieldGet5.continue();
  }
  onOnline() {
    var _classPrivateFieldGet6;
    const observer = this.observers.find(x => x.shouldFetchOnReconnect());
    observer === null || observer === void 0 || observer.refetch({
      cancelRefetch: false
    });
    (_classPrivateFieldGet6 = _classPrivateFieldGet(_retryer, this)) === null || _classPrivateFieldGet6 === void 0 || _classPrivateFieldGet6.continue();
  }
  addObserver(observer) {
    if (!this.observers.includes(observer)) {
      this.observers.push(observer);
      this.clearGcTimeout();
      _classPrivateFieldGet(_cache, this).notify({
        type: "observerAdded",
        query: this,
        observer
      });
    }
  }
  removeObserver(observer) {
    if (this.observers.includes(observer)) {
      this.observers = this.observers.filter(x => x !== observer);
      if (!this.observers.length) {
        if (_classPrivateFieldGet(_retryer, this)) {
          if (_classPrivateFieldGet(_abortSignalConsumed, this)) {
            _classPrivateFieldGet(_retryer, this).cancel({
              revert: true
            });
          } else {
            _classPrivateFieldGet(_retryer, this).cancelRetry();
          }
        }
        this.scheduleGc();
      }
      _classPrivateFieldGet(_cache, this).notify({
        type: "observerRemoved",
        query: this,
        observer
      });
    }
  }
  getObserversCount() {
    return this.observers.length;
  }
  invalidate() {
    if (!this.state.isInvalidated) {
      _assertClassBrand(_Class_brand, this, _dispatch).call(this, {
        type: "invalidate"
      });
    }
  }
  fetch(options, fetchOptions) {
    var _this$options$behavio, _context$fetchOptions;
    if (this.state.fetchStatus !== "idle") {
      if (this.state.data !== void 0 && fetchOptions !== null && fetchOptions !== void 0 && fetchOptions.cancelRefetch) {
        this.cancel({
          silent: true
        });
      } else if (_classPrivateFieldGet(_retryer, this)) {
        _classPrivateFieldGet(_retryer, this).continueRetry();
        return _classPrivateFieldGet(_retryer, this).promise;
      }
    }
    if (options) {
      this.setOptions(options);
    }
    if (!this.options.queryFn) {
      const observer = this.observers.find(x => x.options.queryFn);
      if (observer) {
        this.setOptions(observer.options);
      }
    }
    if (process.env.NODE_ENV !== "production") {
      if (!Array.isArray(this.options.queryKey)) {
        console.error(`As of v4, queryKey needs to be an Array. If you are using a string like 'repoData', please change it to an Array, e.g. ['repoData']`);
      }
    }
    const abortController = new AbortController();
    const addSignalProperty = object => {
      Object.defineProperty(object, "signal", {
        enumerable: true,
        get: () => {
          _classPrivateFieldSet(_abortSignalConsumed, this, true);
          return abortController.signal;
        }
      });
    };
    const fetchFn = () => {
      const queryFn = ensureQueryFn(this.options, fetchOptions);
      const createQueryFnContext = () => {
        const queryFnContext2 = {
          client: _classPrivateFieldGet(_client, this),
          queryKey: this.queryKey,
          meta: this.meta
        };
        addSignalProperty(queryFnContext2);
        return queryFnContext2;
      };
      const queryFnContext = createQueryFnContext();
      _classPrivateFieldSet(_abortSignalConsumed, this, false);
      if (this.options.persister) {
        return this.options.persister(queryFn, queryFnContext, this);
      }
      return queryFn(queryFnContext);
    };
    const createFetchContext = () => {
      const context2 = {
        fetchOptions,
        options: this.options,
        queryKey: this.queryKey,
        client: _classPrivateFieldGet(_client, this),
        state: this.state,
        fetchFn
      };
      addSignalProperty(context2);
      return context2;
    };
    const context = createFetchContext();
    (_this$options$behavio = this.options.behavior) === null || _this$options$behavio === void 0 || _this$options$behavio.onFetch(context, this);
    _classPrivateFieldSet(_revertState, this, this.state);
    if (this.state.fetchStatus === "idle" || this.state.fetchMeta !== ((_context$fetchOptions = context.fetchOptions) === null || _context$fetchOptions === void 0 ? void 0 : _context$fetchOptions.meta)) {
      var _context$fetchOptions2;
      _assertClassBrand(_Class_brand, this, _dispatch).call(this, {
        type: "fetch",
        meta: (_context$fetchOptions2 = context.fetchOptions) === null || _context$fetchOptions2 === void 0 ? void 0 : _context$fetchOptions2.meta
      });
    }
    const onError = error => {
      if (!(isCancelledError(error) && error.silent)) {
        _assertClassBrand(_Class_brand, this, _dispatch).call(this, {
          type: "error",
          error
        });
      }
      if (!isCancelledError(error)) {
        var _classPrivateFieldGet7, _classPrivateFieldGet8, _classPrivateFieldGet9, _classPrivateFieldGet10;
        (_classPrivateFieldGet7 = (_classPrivateFieldGet8 = _classPrivateFieldGet(_cache, this).config).onError) === null || _classPrivateFieldGet7 === void 0 || _classPrivateFieldGet7.call(_classPrivateFieldGet8, error, this);
        (_classPrivateFieldGet9 = (_classPrivateFieldGet10 = _classPrivateFieldGet(_cache, this).config).onSettled) === null || _classPrivateFieldGet9 === void 0 || _classPrivateFieldGet9.call(_classPrivateFieldGet10, this.state.data, error, this);
      }
      this.scheduleGc();
    };
    _classPrivateFieldSet(_retryer, this, createRetryer({
      initialPromise: fetchOptions === null || fetchOptions === void 0 ? void 0 : fetchOptions.initialPromise,
      fn: context.fetchFn,
      abort: abortController.abort.bind(abortController),
      onSuccess: data => {
        var _classPrivateFieldGet11, _classPrivateFieldGet12, _classPrivateFieldGet13, _classPrivateFieldGet14;
        if (data === void 0) {
          if (process.env.NODE_ENV !== "production") {
            console.error(`Query data cannot be undefined. Please make sure to return a value other than undefined from your query function. Affected query key: ${this.queryHash}`);
          }
          onError(new Error(`${this.queryHash} data is undefined`));
          return;
        }
        try {
          this.setData(data);
        } catch (error) {
          onError(error);
          return;
        }
        (_classPrivateFieldGet11 = (_classPrivateFieldGet12 = _classPrivateFieldGet(_cache, this).config).onSuccess) === null || _classPrivateFieldGet11 === void 0 || _classPrivateFieldGet11.call(_classPrivateFieldGet12, data, this);
        (_classPrivateFieldGet13 = (_classPrivateFieldGet14 = _classPrivateFieldGet(_cache, this).config).onSettled) === null || _classPrivateFieldGet13 === void 0 || _classPrivateFieldGet13.call(_classPrivateFieldGet14, data, this.state.error, this);
        this.scheduleGc();
      },
      onError,
      onFail: (failureCount, error) => {
        _assertClassBrand(_Class_brand, this, _dispatch).call(this, {
          type: "failed",
          failureCount,
          error
        });
      },
      onPause: () => {
        _assertClassBrand(_Class_brand, this, _dispatch).call(this, {
          type: "pause"
        });
      },
      onContinue: () => {
        _assertClassBrand(_Class_brand, this, _dispatch).call(this, {
          type: "continue"
        });
      },
      retry: context.options.retry,
      retryDelay: context.options.retryDelay,
      networkMode: context.options.networkMode,
      canRun: () => true
    }));
    return _classPrivateFieldGet(_retryer, this).start();
  }
});
function _dispatch(action) {
  const reducer = state => {
    var _action$meta, _action$dataUpdatedAt;
    switch (action.type) {
      case "failed":
        return {
          ...state,
          fetchFailureCount: action.failureCount,
          fetchFailureReason: action.error
        };
      case "pause":
        return {
          ...state,
          fetchStatus: "paused"
        };
      case "continue":
        return {
          ...state,
          fetchStatus: "fetching"
        };
      case "fetch":
        return {
          ...state,
          ...fetchState(state.data, this.options),
          fetchMeta: (_action$meta = action.meta) !== null && _action$meta !== void 0 ? _action$meta : null
        };
      case "success":
        _classPrivateFieldSet(_revertState, this, void 0);
        return {
          ...state,
          data: action.data,
          dataUpdateCount: state.dataUpdateCount + 1,
          dataUpdatedAt: (_action$dataUpdatedAt = action.dataUpdatedAt) !== null && _action$dataUpdatedAt !== void 0 ? _action$dataUpdatedAt : Date.now(),
          error: null,
          isInvalidated: false,
          status: "success",
          ...(!action.manual && {
            fetchStatus: "idle",
            fetchFailureCount: 0,
            fetchFailureReason: null
          })
        };
      case "error":
        const error = action.error;
        if (isCancelledError(error) && error.revert && _classPrivateFieldGet(_revertState, this)) {
          return {
            ..._classPrivateFieldGet(_revertState, this),
            fetchStatus: "idle"
          };
        }
        return {
          ...state,
          error,
          errorUpdateCount: state.errorUpdateCount + 1,
          errorUpdatedAt: Date.now(),
          fetchFailureCount: state.fetchFailureCount + 1,
          fetchFailureReason: error,
          fetchStatus: "idle",
          status: "error"
        };
      case "invalidate":
        return {
          ...state,
          isInvalidated: true
        };
      case "setState":
        return {
          ...state,
          ...action.state
        };
    }
  };
  this.state = reducer(this.state);
  notifyManager.batch(() => {
    this.observers.forEach(observer => {
      observer.onQueryUpdate();
    });
    _classPrivateFieldGet(_cache, this).notify({
      query: this,
      type: "updated",
      action
    });
  });
}
function fetchState(data, options) {
  return {
    fetchFailureCount: 0,
    fetchFailureReason: null,
    fetchStatus: canFetch(options.networkMode) ? "fetching" : "paused",
    ...(data === void 0 && {
      error: null,
      status: "pending"
    })
  };
}
function getDefaultState(options) {
  const data = typeof options.initialData === "function" ? options.initialData() : options.initialData;
  const hasData = data !== void 0;
  const initialDataUpdatedAt = hasData ? typeof options.initialDataUpdatedAt === "function" ? options.initialDataUpdatedAt() : options.initialDataUpdatedAt : 0;
  return {
    data,
    dataUpdateCount: 0,
    dataUpdatedAt: hasData ? initialDataUpdatedAt !== null && initialDataUpdatedAt !== void 0 ? initialDataUpdatedAt : Date.now() : 0,
    error: null,
    errorUpdateCount: 0,
    errorUpdatedAt: 0,
    fetchFailureCount: 0,
    fetchFailureReason: null,
    fetchMeta: null,
    isInvalidated: false,
    status: hasData ? "success" : "pending",
    fetchStatus: "idle"
  };
}
export { Query, fetchState };